import { memo } from "react";
import { TableHead } from "@/components/ui/table";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SortDirection } from "./types";

const renderSortIcon = ({
  isSorted,
  sortDirection,
  className,
}: {
  isSorted: boolean;
  sortDirection: SortDirection;
  className?: string;
}) => {
  if (!isSorted) return <ArrowUpDown className={className} />;

  if (sortDirection === "asc") return <ArrowUp className={className} />;
  if (sortDirection === "desc") return <ArrowDown className={className} />;

  return <ArrowUpDown className={className} />;
};

interface TableHeaderCellProps {
  label: string;
  columnKey: string;
  sortColumn: string | null;
  sortDirection: SortDirection;
  onSort: (key: string) => void;
  isGroup?: boolean;
  colSpan?: number;
  rowSpan?: number;
}

export const TableHeaderCell = memo(function TableHeaderCell({
  label,
  columnKey,
  sortColumn,
  sortDirection,
  onSort,
  isGroup = false,
  colSpan,
  rowSpan,
}: TableHeaderCellProps) {
  const isSorted = sortColumn === columnKey;

  if (isGroup) {
    return (
      <TableHead
        colSpan={colSpan}
        className="border-x border-b border-border bg-muted text-center font-bold text-foreground"
      >
        {label}
      </TableHead>
    );
  }

  return (
    <TableHead
      rowSpan={rowSpan}
      className={cn(
        "whitespace-nowrap border-b border-border bg-muted/50 px-4 py-3 align-bottom font-semibold text-foreground",
        "cursor-pointer select-none hover:bg-muted",
        isSorted && "bg-accent",
      )}
      onClick={() => onSort(columnKey)}
      title={label}
    >
      <div className="flex items-center gap-2">
        <span className="truncate max-w-[100px] block">{label}</span>
        {renderSortIcon({
          isSorted,
          sortDirection,
          className: cn(
            "h-4 w-4 shrink-0 transition-opacity",
            isSorted ? "opacity-100" : "opacity-40",
          ),
        })}
      </div>
    </TableHead>
  );
});
